import { formatUsdCurrency, ledgerDisplayStatus, signedNetAmount, usdLedgerCreatedAt, } from '../portal-usd-wallet/ledgerUtils';
import { buildFeeBreakdownMetaKvs, buildNetWithdrawalFeeNote, buildSingleFeeMetaKv, } from './feeBreakdownDisplay';
function pick(row, camel, snake) {
    const v = row[camel];
    if (v !== undefined && v !== null)
        return v;
    return row[snake];
}
function toNum(v) {
    if (v === undefined || v === null || v === '')
        return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}
function metaNum(meta, ...keys) {
    for (const k of keys) {
        const n = toNum(meta[k]);
        if (n != null)
            return n;
    }
    return null;
}
function formatCredits(n) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}
function formatRatePerCredit(rate) {
    return `$${rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} per credit`;
}
export function resolveUsdLedgerFee(row) {
    var _a, _b, _c, _d;
    const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const amountGross = (_a = toNum(pick(row, 'amountGross', 'amount_gross'))) !== null && _a !== void 0 ? _a : 0;
    const amountNet = (_b = toNum(pick(row, 'amountNet', 'amount_net'))) !== null && _b !== void 0 ? _b : 0;
    const platformFee = (_d = toNum((_c = meta.platformFeeAmount) !== null && _c !== void 0 ? _c : meta.platform_fee_amount)) !== null && _d !== void 0 ? _d : 0;
    const feeFromDelta = Math.abs(amountGross - amountNet) > 0.0000001 ? Math.abs(amountGross - amountNet) : 0;
    const feeAmount = platformFee > 0 ? platformFee : feeFromDelta;
    const hasFee = feeAmount > 0.0000001;
    return { hasFee, feeAmount, amountGross, amountNet };
}
function formatUsdDeduction(n) {
    return `-${formatUsdCurrency(n)}`;
}
function isNetWithdrawalPayout(type, refTypeLower, meta, purpose) {
    if (type !== 'withdrawal')
        return false;
    return (purpose.includes('payout') ||
        meta.payoutPhase != null ||
        refTypeLower === 'usd_withdrawal_request');
}
function resolveNetPayoutUsd(meta, amountGross, amountNet, usdMagnitude) {
    const requested = metaNum(meta, 'requestedNetUsd', 'requested_net_usd');
    if (amountNet > 0 && (amountGross <= 0 || amountNet < amountGross))
        return amountNet;
    if (requested != null && requested > 0)
        return requested;
    if (amountNet > 0)
        return amountNet;
    return usdMagnitude;
}
function buildNetWithdrawalPayoutFeeSection(row, netPayoutUsd, totalDebitedUsd, totalFeeUsd) {
    var _a;
    const breakdown = row.feeBreakdown;
    const itemized = buildFeeBreakdownMetaKvs(breakdown !== null && breakdown !== void 0 ? breakdown : undefined, { netWithdrawalPayout: true });
    if (totalFeeUsd < 0.0001 && itemized.length === 0)
        return undefined;
    let feeLines;
    if (itemized.length) {
        feeLines = itemized.map((line) => ({
            label: line.label,
            value: line.value,
            isDeduction: false,
        }));
    }
    else {
        feeLines = [
            {
                label: 'Total fee',
                value: formatUsdCurrency(totalFeeUsd),
                isDeduction: false,
            },
        ];
    }
    const formulaText = `${formatUsdCurrency(netPayoutUsd)} net payout + ${formatUsdCurrency(totalFeeUsd)} total fees = ${formatUsdCurrency(totalDebitedUsd)} total wallet deduction`;
    const feeNote = breakdown && ((_a = breakdown.items) === null || _a === void 0 ? void 0 : _a.length)
        ? buildNetWithdrawalFeeNote(netPayoutUsd, breakdown)
        : undefined;
    return {
        layout: 'additive',
        isMoneyIn: false,
        grossLabel: 'Requested withdrawal',
        grossUsd: netPayoutUsd,
        feeLines,
        netLabel: 'Total debited from USD Wallet',
        netUsd: totalDebitedUsd,
        formulaText,
        feeNote,
    };
}
function isMoneyInType(type, signed) {
    const t = type.toLowerCase();
    if (t === 'deposit')
        return true;
    if (t === 'withdrawal' || t === 'purchase_credits')
        return false;
    if (t === 'reversal')
        return signed >= 0;
    if (t === 'adjustment')
        return signed >= 0;
    return signed >= 0;
}
function buildUsdFeeSection(row, type, signed, skipFee) {
    var _a;
    if (skipFee)
        return undefined;
    const { hasFee, feeAmount, amountGross, amountNet } = resolveUsdLedgerFee(row);
    const breakdown = row.feeBreakdown;
    const itemized = buildFeeBreakdownMetaKvs(breakdown !== null && breakdown !== void 0 ? breakdown : undefined);
    if (!hasFee && itemized.length === 0)
        return undefined;
    const isMoneyIn = isMoneyInType(type, signed);
    const grossUsd = amountGross > 0
        ? amountGross
        : Math.abs(signed) + ((_a = breakdown === null || breakdown === void 0 ? void 0 : breakdown.totalFeeUsd) !== null && _a !== void 0 ? _a : feeAmount);
    const netUsd = amountNet > 0 ? amountNet : Math.abs(signed);
    let feeLines;
    if (itemized.length) {
        feeLines = itemized.map((line) => ({
            label: line.label,
            value: line.value,
            isDeduction: true,
        }));
    }
    else {
        const single = buildSingleFeeMetaKv(grossUsd, feeAmount);
        feeLines = [{ label: single.label, value: single.value, isDeduction: true }];
    }
    return {
        layout: 'deductive',
        isMoneyIn,
        grossLabel: isMoneyIn ? 'Amount received' : 'Gross amount',
        grossUsd,
        feeLines,
        netLabel: isMoneyIn ? 'Net added to wallet' : 'Net wallet impact',
        netUsd,
    };
}
function withFeeSection(view, row, type, signed, skipFee) {
    const feeSection = buildUsdFeeSection(row, type, signed, skipFee);
    if (!feeSection)
        return view;
    return { ...view, feeSection };
}
function humanStatus(st) {
    const s = st.toLowerCase();
    if (s === 'completed')
        return { label: 'Completed', tone: 'completed' };
    if (s === 'processing')
        return { label: 'Processing', tone: 'processing' };
    if (s === 'pending')
        return { label: 'Pending', tone: 'pending' };
    if (s === 'failed')
        return { label: 'Failed', tone: 'failed' };
    if (s === 'cancelled')
        return { label: 'Cancelled', tone: 'cancelled' };
    return { label: st || '—', tone: 'neutral' };
}
/** Business-friendly read model for portal USD detail modal (non-tech default view). */
export function buildUsdBusinessView(row, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const type = String((_a = pick(row, 'type', 'type')) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const refType = String((_b = pick(row, 'referenceType', 'reference_type')) !== null && _b !== void 0 ? _b : '').trim();
    const refTypeLower = refType.toLowerCase();
    const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const st = ledgerDisplayStatus(row);
    const { label: statusLabel, tone: statusTone } = humanStatus(st);
    const dateLabel = usdLedgerCreatedAt(row, timezone);
    const balanceBefore = toNum(pick(row, 'balanceNetBefore', 'balance_net_before'));
    const balanceAfter = toNum(pick(row, 'balanceNetAfter', 'balance_net_after'));
    const signed = signedNetAmount(row);
    const usdMagnitude = Math.abs(signed);
    const creditsOut = metaNum(meta, 'credits_out', 'creditsOut');
    const costRate = metaNum(meta, 'cost_rate_applied', 'cost_rate', 'costRateApplied', 'costRate');
    const purpose = String((_c = meta.purpose) !== null && _c !== void 0 ? _c : '').toLowerCase();
    const isBalanceToCredits = refTypeLower === 'balance_to_credits' ||
        purpose === 'balance_to_credits' ||
        type === 'purchase_credits' ||
        (type === 'withdrawal' && creditsOut != null);
    const isSettlement = refTypeLower === 'balance_to_credits_settlement';
    const support = {
        ledgerId: String((_d = pick(row, 'id', 'id')) !== null && _d !== void 0 ? _d : ''),
        transactionId: String((_e = pick(row, 'transactionId', 'transaction_id')) !== null && _e !== void 0 ? _e : ''),
        referenceType: refType || '—',
        referenceId: String((_f = pick(row, 'referenceId', 'reference_id')) !== null && _f !== void 0 ? _f : ''),
        metadataJson: Object.keys(meta).length > 0 ? JSON.stringify(meta, null, 2) : null,
    };
    if (isBalanceToCredits && (type === 'withdrawal' || type === 'purchase_credits')) {
        const spent = usdMagnitude || toNum(pick(row, 'amountGross', 'amount_gross')) || 0;
        const credits = creditsOut !== null && creditsOut !== void 0 ? creditsOut : (costRate && costRate > 0 ? spent / costRate : null);
        const rate = costRate !== null && costRate !== void 0 ? costRate : (credits && spent > 0 ? spent / credits : null);
        return withFeeSection({
            title: 'USD converted to credits',
            subtitle: 'Your USD balance was used to add credits to your credit wallet.',
            summaryLine: 'This transaction converted USD from your USD wallet into credits.',
            statusLabel,
            statusTone,
            dateLabel,
            hero: {
                spentUsd: spent,
                receivedCredits: credits !== null && credits !== void 0 ? credits : undefined,
                ratePerCredit: rate !== null && rate !== void 0 ? rate : undefined,
            },
            balanceRows: [
                { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                { label: 'USD used', value: formatUsdCurrency(spent), emphasize: true },
                { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
            ],
            relatedCreditCopy: 'A matching credit transaction was created for the credits added to your wallet.',
            support,
        }, row, type, signed, true);
    }
    if (isSettlement && type === 'deposit') {
        const added = usdMagnitude || toNum(pick(row, 'amountNet', 'amount_net')) || 0;
        return withFeeSection({
            title: 'USD settlement received',
            subtitle: 'Funds were added to your USD wallet as part of a credit conversion settlement.',
            statusLabel,
            statusTone,
            dateLabel,
            hero: { addedUsd: added },
            balanceRows: [
                { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                { label: 'USD added', value: formatUsdCurrency(added), emphasize: true },
                { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
            ],
            relatedUsdCopy: 'This settlement is linked to an earlier USD conversion activity.',
            relatedUsdLinkLabel: 'View related USD activity',
            support,
        }, row, type, signed, true);
    }
    if (type === 'deposit') {
        const added = signed > 0 ? signed : usdMagnitude;
        return withFeeSection({
            title: 'USD added to wallet',
            subtitle: 'Your USD wallet balance increased.',
            statusLabel,
            statusTone,
            dateLabel,
            hero: { addedUsd: added },
            balanceRows: [
                { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                { label: 'USD added', value: formatUsdCurrency(added), emphasize: true },
                { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
            ],
            support,
        }, row, type, signed);
    }
    if (type === 'withdrawal') {
        const isPayout = isNetWithdrawalPayout(type, refTypeLower, meta, purpose);
        const amountGross = (_g = toNum(pick(row, 'amountGross', 'amount_gross'))) !== null && _g !== void 0 ? _g : 0;
        const amountNet = (_h = toNum(pick(row, 'amountNet', 'amount_net'))) !== null && _h !== void 0 ? _h : 0;
        const breakdown = row.feeBreakdown;
        const { feeAmount } = resolveUsdLedgerFee(row);
        const totalFeeUsd = (_j = breakdown === null || breakdown === void 0 ? void 0 : breakdown.totalFeeUsd) !== null && _j !== void 0 ? _j : feeAmount;
        const totalDebited = amountGross > 0 ? amountGross : usdMagnitude;
        const netPayout = resolveNetPayoutUsd(meta, amountGross, amountNet, usdMagnitude);
        const usePayoutFeeLayout = isPayout && totalFeeUsd > 0.0001 && netPayout < totalDebited - 0.0001;
        if (usePayoutFeeLayout) {
            const payoutSubtitle = statusTone === 'completed'
                ? 'The withdrawal has been completed and sent to your registered crypto wallet.'
                : statusTone === 'failed' || statusTone === 'cancelled'
                    ? 'This withdrawal did not complete successfully.'
                    : 'A withdrawal was requested from your USD wallet.';
            const feeSection = buildNetWithdrawalPayoutFeeSection(row, netPayout, totalDebited, totalFeeUsd);
            return {
                title: 'USD withdrawal',
                subtitle: payoutSubtitle,
                statusLabel,
                statusTone,
                dateLabel,
                withdrawalPayoutLayout: true,
                hero: {
                    totalDebitedUsd: totalDebited,
                    netPayoutUsd: netPayout,
                },
                balanceRows: [
                    { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                    {
                        label: 'Requested withdrawal',
                        value: formatUsdDeduction(netPayout),
                        deduction: true,
                    },
                    {
                        label: 'Total fees',
                        value: formatUsdDeduction(totalFeeUsd),
                        deduction: true,
                    },
                    {
                        label: 'Total debited from USD Wallet',
                        value: formatUsdDeduction(totalDebited),
                        emphasize: true,
                        deduction: true,
                    },
                    { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
                ],
                feeSection,
                support,
            };
        }
        const withdrawn = usdMagnitude;
        return withFeeSection({
            title: isPayout ? 'USD withdrawal' : 'USD withdrawn',
            subtitle: isPayout
                ? statusTone === 'completed'
                    ? 'The withdrawal has been completed and sent to your registered crypto wallet.'
                    : 'A withdrawal was requested from your USD wallet.'
                : 'USD was deducted from your USD wallet balance.',
            statusLabel,
            statusTone,
            dateLabel,
            hero: { withdrawnUsd: withdrawn },
            balanceRows: [
                { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                { label: 'USD withdrawn', value: formatUsdCurrency(withdrawn), emphasize: true },
                { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
            ],
            support,
        }, row, type, signed);
    }
    if (type === 'adjustment' || type === 'reversal') {
        const delta = signed;
        const isRev = type === 'reversal';
        return withFeeSection({
            title: isRev ? 'Wallet activity reversed' : 'Wallet adjustment',
            subtitle: isRev
                ? 'A previous wallet activity was reversed on your USD balance.'
                : 'Your USD balance was updated by an adjustment.',
            statusLabel,
            statusTone,
            dateLabel,
            balanceRows: [
                { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
                {
                    label: delta >= 0 ? 'USD added' : 'USD used',
                    value: formatUsdCurrency(Math.abs(delta)),
                    emphasize: true,
                },
                { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
            ],
            support,
        }, row, type, signed);
    }
    return withFeeSection({
        title: 'USD wallet activity',
        subtitle: 'Summary of how this activity affected your USD wallet balance.',
        statusLabel,
        statusTone,
        dateLabel,
        balanceRows: [
            { label: 'USD balance before', value: formatUsdCurrency(balanceBefore !== null && balanceBefore !== void 0 ? balanceBefore : 0) },
            {
                label: 'Change',
                value: formatUsdCurrency(signed),
                emphasize: true,
            },
            { label: 'USD balance after', value: formatUsdCurrency(balanceAfter !== null && balanceAfter !== void 0 ? balanceAfter : 0) },
        ],
        support,
    }, row, type, signed);
}
export function formatCreditsDisplay(n) {
    return `${formatCredits(n)} credits`;
}
